import axios, {AxiosError} from 'axios'
import {z} from 'zod'
import {envVars} from '@/envVars.ts'
import {attemptRefresh} from '@/axiosInstance.ts'
import {useAuthStore} from '@features/user/store/store.ts'
import {LOCAL_STORAGE_ACCESS_TOKEN, LOCAL_STORAGE_REFRESH_TOKEN} from '@features/user/constants.ts'

/**
 * The chatbot lives on its own backend service, so it needs an axios instance
 * of its own. Auth mirrors `axiosInstanceNode`: the bearer token identifies the
 * photographer and the backend derives the rest from it — no host password is
 * ever sent. The service also requires that same token mirrored in an
 * `x-navigate-id` header on every request.
 */
const chatbotAxios = axios.create({
    baseURL: envVars.VITE_AI_CHATBOT_API_URL,
    headers: {'Content-Type': 'application/json'}
})

chatbotAxios.interceptors.request.use(request => {
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN)
    if (token) {
        request.headers['Authorization'] = `Bearer ${token}`
        request.headers['x-navigate-id'] = token
    }
    return request
})

chatbotAxios.interceptors.response.use(
    response => response,
    async error => {
        const statusCode = error?.response?.status
        const originalRequest = error.config

        if (statusCode === 401) {
            const refreshToken = useAuthStore.getState().refreshToken
            localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN)
            if (refreshToken && !originalRequest._retry) {
                originalRequest._retry = true
                await attemptRefresh(refreshToken)
                return chatbotAxios.request(originalRequest)
            }
            useAuthStore.getState().resetAuthTokens()
        }
        return Promise.reject(error)
    }
)

export interface HttpInitChatSessionOptions {
    language: string
    /**
     * Code of the assignment the user is looking at, when the chat is opened
     * from an assignment page. Lets the backend scope the session to that
     * assignment instead of resolving a generic one.
     */
    assignmentCode?: string
}

export const HttpInitChatSessionResponse = z.object({
    data: z.object({
        sessionId: z.string(),
        currentQuote: z
            .object({
                id: z.string(),
                title: z.string()
            })
            .nullish(),
        suggestedQuestions: z.array(z.string()).nullish()
    })
})

export const httpInitChatSession = (payload: HttpInitChatSessionOptions) =>
    chatbotAxios.post('/navigate/v1/chat/init', payload)

export interface HttpSendChatMessageOptions {
    text: string
    sessionId: string
    confirmAction?: boolean
    value?: string | number
    anythingElse?: boolean
    wantsToComment?: boolean
}

export const httpSendChatMessage = async (
    payload: HttpSendChatMessageOptions,
    signal?: AbortSignal
): Promise<ReadableStream<Uint8Array>> => {
    try {
        const {data} = await chatbotAxios.post<ReadableStream<Uint8Array>>('/navigate/v1/chat/message', payload, {
            adapter: 'fetch',
            responseType: 'stream',
            signal
        })
        return data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const axiosError = error as AxiosError<{code?: string; message?: string}>
            const status = axiosError.response?.status
            const errorData = axiosError.response?.data
            if (status === 403 && errorData?.code === 'INVALID_SESSION') {
                throw new InvalidSessionError(errorData.message ?? 'Invalid session')
            }
            throw new Error(`Chat API error: ${status ?? 'unknown'}`)
        }
        throw error
    }
}

export class InvalidSessionError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'InvalidSessionError'
    }
}
